/* 把同步接進 React。金鑰只存在記憶體：重新載入頁面就要重新解鎖（金鑰不落地）。
   伺服器網址與金鑰紀錄存在本機，所以離線也進得了設定畫面。 */
import { useCallback, useEffect, useMemo, useState } from "react";
import type { IdbRepository } from "../data/repository.ts";
import type { KeyRecord } from "../crypto/keys.ts";
import {
  createKeyRecord, unlockWithPassphrase, unlockWithRecoveryCode, BadPassphraseError, authKeyFromBase,
} from "../crypto/keys.ts";
import { HttpTransport, SyncEngine } from "../sync/client.ts";
import type { SyncOutcome } from "../sync/client.ts";
import {
  loadSyncConfig, saveSyncConfig, loadKeyRecord, saveKeyRecord, normalizeServerUrl, validatePassphrase,
} from "../sync/session.ts";
import type { SyncConfig, UnlockedSession } from "../sync/session.ts";

export interface SyncState {
  configured: boolean;
  unlocked: boolean;
  config: SyncConfig | null;
  keyId: string;
  running: boolean;
  pending: number;
  lastSyncAt: number | null;
  lastResult: SyncOutcome | null;
  error: string | null;
  recoveryCode: string | null;
  busy: boolean;
}

const IDLE: SyncState = {
  configured: false, unlocked: false, config: null, keyId: "", running: false, pending: 0,
  lastSyncAt: null, lastResult: null, error: null, recoveryCode: null, busy: false,
};

export function useSync(repo: IdbRepository | null, deviceId: string, onRemoteApplied: () => Promise<void>) {
  const [record, setRecord] = useState<KeyRecord | null>(null);
  const [session, setSession] = useState<UnlockedSession | null>(null);
  const [state, setState] = useState<SyncState>(IDLE);

  const refreshPending = useCallback(async () => {
    if (!repo) return 0;
    const n = (await repo.outbox()).length;
    setState((s) => ({ ...s, pending: n }));
    return n;
  }, [repo]);

  useEffect(() => {
    if (!repo) return;
    void (async () => {
      const [config, keyRecord, lastSyncAt] = await Promise.all([
        loadSyncConfig(repo), loadKeyRecord(repo), repo.getMeta<number>("sync.lastSyncAt"),
      ]);
      setRecord(keyRecord);
      setState((s) => ({ ...s, configured: !!keyRecord && !!config, config,
        keyId: config?.keyId ?? "", lastSyncAt: lastSyncAt ?? null }));
      await refreshPending();
    })();
  }, [repo, refreshPending]);

  const engine = useMemo(() => {
    if (!repo || !session || !state.config || !state.config.serverUrl) return null;
    return new SyncEngine({
      repo, transport: new HttpTransport(state.config.serverUrl),
      keys: { keyId: session.keyId, main: session.material.main, adult: session.material.adult },
      authKey: session.authKeyB64, deviceId,
    });
  }, [repo, session, state.config, deviceId]);

  /** 建立新的同步空間：產生金鑰、存本機、上傳金鑰紀錄、回傳救援碼。 */
  const createAccount = useCallback(async (serverUrl: string, passphrase: string, confirm: string) => {
    if (!repo) throw new Error("儲存層還沒準備好");
    const check = validatePassphrase(passphrase, confirm);
    if (!check.ok) throw new Error(check.key);
    const url = normalizeServerUrl(serverUrl);
    setState((s) => ({ ...s, busy: true, error: null }));
    try {
      const created = await createKeyRecord(passphrase);
      const config: SyncConfig = { serverUrl: url, keyId: created.record.keyId };
      /* 先落地再上傳：任何時刻中斷，本機都有紀錄，救援碼也不會白給。 */
      await saveKeyRecord(repo, created.record);
      await saveSyncConfig(repo, config);
      if (url) await new HttpTransport(url).putKeyRecord(created.record.keyId, created.record);
      setRecord(created.record);
      setSession({ keyId: created.record.keyId, material: created.material, authKeyB64: created.authKeyB64 });
      setState((s) => ({ ...s, configured: true, unlocked: true, config, keyId: created.record.keyId,
        recoveryCode: created.recoveryCode, busy: false, error: null }));
      void refreshPending();
    } catch (e) {
      setState((s) => ({ ...s, busy: false, error: e instanceof Error ? e.message : String(e) }));
      throw e;
    }
  }, [repo, refreshPending]);

  /** 加入既有的同步空間：向伺服器取金鑰紀錄再解鎖。 */
  const joinAccount = useCallback(async (serverUrl: string, keyId: string, passphrase: string) => {
    if (!repo) throw new Error("儲存層還沒準備好");
    const url = normalizeServerUrl(serverUrl);
    /* 沒有網址時 HttpTransport("") 會發出相對路徑請求，打到 App 自己的靜態伺服器，
       然後拿到一份 HTML 而不是 JSON —— 錯誤訊息會完全誤導人。所以這裡先擋。 */
    if (!url) {
      setState((s) => ({ ...s, error: "sync.serverUrl.required" }));
      throw new Error("sync.serverUrl.required");
    }
    setState((s) => ({ ...s, busy: true, error: null }));
    try {
      const fetched = await new HttpTransport(url).getKeyRecord(keyId.trim());
      if (!fetched) throw new Error("找不到這組同步空間，請確認識別碼與伺服器網址。");
      const unlocked = await unlockWithPassphrase(fetched, passphrase);
      await saveKeyRecord(repo, fetched);
      const config: SyncConfig = { serverUrl: url, keyId: fetched.keyId };
      await saveSyncConfig(repo, config);
      setRecord(fetched);
      setSession({ keyId: fetched.keyId, material: unlocked.material, authKeyB64: unlocked.authKeyB64 });
      setState((s) => ({ ...s, configured: true, unlocked: true, config, keyId: fetched.keyId,
        busy: false, error: null }));
      void refreshPending();
    } catch (e) {
      const message = e instanceof BadPassphraseError ? "sync.badPassphrase"
        : e instanceof Error ? e.message : String(e);
      setState((s) => ({ ...s, busy: false, error: message }));
      throw e;
    }
  }, [repo, refreshPending]);

  const unlock = useCallback(async (secret: string, useRecovery: boolean) => {
    if (!record) throw new Error("這台裝置沒有金鑰紀錄。");
    setState((s) => ({ ...s, busy: true, error: null }));
    try {
      const result = useRecovery
        ? await unlockWithRecoveryCode(record, secret)
        : await unlockWithPassphrase(record, secret);
      await saveKeyRecord(repo!, record);
      setSession({ keyId: record.keyId, material: result.material, authKeyB64: result.authKeyB64 });
      setState((s) => ({ ...s, unlocked: true, busy: false, error: null }));
    } catch (e) {
      const message = e instanceof BadPassphraseError ? "sync.badPassphrase"
        : e instanceof Error ? e.message : String(e);
      setState((s) => ({ ...s, busy: false, error: message }));
      throw e;
    }
  }, [record, repo]);

  const lock = useCallback(() => {
    setSession(null);
    setState((s) => ({ ...s, unlocked: false }));
  }, []);

  const dismissRecovery = useCallback(() => setState((s) => ({ ...s, recoveryCode: null })), []);

  const syncNow = useCallback(async () => {
    if (!engine || !repo) return null;
    setState((s) => ({ ...s, running: true, error: null }));
    const out = await engine.runOnce();
    const at = Date.now();
    if (!out.error) await repo.setMeta("sync.lastSyncAt", at);
    const pending = (await repo.outbox()).length;
    setState((s) => ({ ...s, running: false, lastResult: out, lastSyncAt: out.error ? s.lastSyncAt : at,
      error: out.error ?? null, pending }));
    if (out.applied > 0 || out.conflicts > 0) await onRemoteApplied();
    return out;
  }, [engine, repo, refreshPending, onRemoteApplied]);

  const setServerUrl = useCallback(async (url: string) => {
    if (!repo || !state.config) return;
    const config = { ...state.config, serverUrl: normalizeServerUrl(url) };
    await saveSyncConfig(repo, config);
    setState((s) => ({ ...s, config }));
  }, [repo, state.config]);

  /** 供設定頁顯示：這把 DEK 派生出的驗證金鑰前幾碼（證明「同一把金鑰」用，不是秘密）。 */
  const authKeyPreview = useMemo(() => {
    if (!session) return "";
    return session.authKeyB64.slice(0, 8);
  }, [session]);

  return { state, syncNow, createAccount, joinAccount, unlock, lock, dismissRecovery, setServerUrl, refreshPending, authKeyPreview };
}

export { authKeyFromBase };
